import React from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useNavigation } from "../../utilities/NavigationContext";
import { useAuth } from "../../utilities/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

/**
 * Forgot Password (Class Component)
 * Uses:
 *  - Honeypot (company field)
 *  - reCAPTCHA v3
 */
class ForgotPassword extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      email: "",
      company: "",
      isSubmitting: false,
    };
  }

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  handleSubmit = async (e) => {
    e.preventDefault();

    const { email, company, isSubmitting } = this.state;
    if (isSubmitting) return;

    if (company && company.trim().length > 0) {
      return;
    }

    const { executeRecaptcha } = this.props;
    if (!executeRecaptcha) {
      toast.error("reCAPTCHA is loading. Please try again.");
      return;
    }

    this.setState({ isSubmitting: true });

    let tokenCap;
    try {
      tokenCap = await executeRecaptcha("forgot_password");
    } catch {
      this.setState({ isSubmitting: false });
      toast.error("reCAPTCHA failed. Please try again.");
      return;
    }

    try {
      await axios.post(`${EndPoint}/user/forgot-password`, {
        email: email.trim(),
        tokenCap,
        company,
      });

      toast.success("Password reset link sent to your email.");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Error sending reset email."
      );
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const { email, company, isSubmitting } = this.state;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-sky-950 to-cyan-900">
        <ToastContainer />

        <div className="mx-auto flex min-h-screen max-w-7xl items-center px-4 py-10">
          <div className="grid w-full overflow-hidden rounded-[32px] border border-white/10 bg-white/10 shadow-2xl backdrop-blur-xl lg:grid-cols-2">
            {/* Left panel */}
            <div className="relative hidden overflow-hidden bg-gradient-to-br from-sky-900 via-cyan-800 to-slate-900 p-10 lg:flex lg:flex-col lg:justify-between">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(56,189,248,0.2),transparent_30%)]" />

              <div className="relative z-10">
                <div className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-100">
                  Caredevo
                </div>

                <h1 className="mt-6 text-4xl font-bold leading-tight text-white">
                  Reset your password
                </h1>

                <p className="mt-4 max-w-md text-sm leading-7 text-sky-100/90">
                  Enter your registered email address and we’ll send you a secure
                  reset link so you can regain access to your account.
                </p>
              </div>

              <div className="relative z-10 grid gap-4">
                <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                  <p className="text-sm font-semibold text-white">
                    Secure recovery
                  </p>
                  <p className="mt-1 text-sm text-sky-100/80">
                    Protected with reCAPTCHA and anti-bot checks before reset
                    emails are sent.
                  </p>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                  <p className="text-sm font-semibold text-white">
                    Fast access
                  </p>
                  <p className="mt-1 text-sm text-sky-100/80">
                    Use your reset link to quickly return to your Caredevo
                    workspace.
                  </p>
                </div>
              </div>
            </div>

            {/* Right panel */}
            <div className="bg-white px-6 py-10 sm:px-10 lg:px-12">
              <div className="mx-auto w-full max-w-md">
                <div className="mb-8 lg:hidden">
                  <div className="inline-flex rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
                    Caredevo
                  </div>
                </div>

                <h2 className="text-3xl font-bold tracking-tight text-slate-900">
                  Forgot Password
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  Enter your registered email to receive a password reset link.
                </p>

                <form onSubmit={this.handleSubmit} className="mt-8 space-y-5">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="you@clinic.com.au"
                      value={email}
                      onChange={this.handleInputChange}
                      required
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100"
                    />
                  </div>

                  {/* Honeypot */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-10000px",
                      top: "auto",
                      width: "1px",
                      height: "1px",
                      overflow: "hidden",
                    }}
                  >
                    <label>
                      Company
                      <input
                        type="text"
                        name="company"
                        value={company}
                        onChange={this.handleInputChange}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full rounded-2xl px-4 py-3 text-sm font-semibold text-white shadow-sm transition ${
                      isSubmitting
                        ? "cursor-not-allowed bg-slate-400"
                        : "bg-sky-700 hover:bg-sky-800"
                    }`}
                  >
                    {isSubmitting ? "Sending..." : "Reset Password"}
                  </button>
                </form>

                <div className="mt-8 border-t border-slate-200 pt-6 text-center text-sm text-slate-500">
                  Remember your password?{" "}
                  <button
                    type="button"
                    onClick={() => this.props.navigate("/login")}
                    className="font-semibold text-sky-700 transition hover:text-sky-900"
                  >
                    Login here
                  </button>
                </div>

                <div className="mt-2 text-center text-sm text-slate-500">
                  Don&apos;t have an account?{" "}
                  <Link
                    to="/register"
                    className="font-semibold text-sky-700 transition hover:text-sky-900"
                  >
                    Register here
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Wrapper (Hooks live here)
 */
const ForgotPasswordWithNavigation = (props) => {
  const navigate = useNavigation();
  const [, setAuth] = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();

  return (
    <ForgotPassword
      {...props}
      navigate={navigate}
      setAuth={setAuth}
      executeRecaptcha={executeRecaptcha}
    />
  );
};

export default ForgotPasswordWithNavigation;